import type {
  BundleAssemblyDTO,
  BundleDisassemblyDTO,
  CreateGoodsReceiptDTO,
  GoodsReceiptListQueryDTO,
  PreviewLandedCostDTO,
  ReorderPlanQueryDTO,
  UpdateGoodsReceiptDTO,
} from "@/modules/inventory/domain/contracts";
import type {
  BundleAssemblyPreviewDTO,
  BundleAssemblyResultDTO,
  BundleDisassemblyPreviewDTO,
  BundleDisassemblyResultDTO,
  GoodsReceiptDetailDTO,
  GoodsReceiptListResult,
  InTransitSummaryDTO,
  LandedCostPreview,
  ReorderHintsDTO,
  ReorderPlanResultDTO,
  StockLotDetailDTO,
  StockWithoutLotSummaryDTO,
} from "@/modules/inventory/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export const inventoryApi = {
  async listReceipts(
    query?: GoodsReceiptListQueryDTO,
  ): Promise<GoodsReceiptListResult> {
    const url = new URL(`${config.apiUrl}/inventory/goods-receipts`);
    if (query?.status) url.searchParams.set("status", query.status);
    if (query?.carrier) url.searchParams.set("carrier", query.carrier);
    if (query?.search) url.searchParams.set("search", query.search);
    return fetcher.get(url.toString());
  },
  async getReceipt(id: string): Promise<GoodsReceiptDetailDTO> {
    return fetcher.get(`${config.apiUrl}/inventory/goods-receipts/${id}`);
  },
  async createReceipt(
    input: CreateGoodsReceiptDTO,
  ): Promise<GoodsReceiptDetailDTO> {
    return fetcher.post(`${config.apiUrl}/inventory/goods-receipts`, input);
  },
  async updateReceipt(
    id: string,
    input: UpdateGoodsReceiptDTO,
  ): Promise<GoodsReceiptDetailDTO> {
    return fetcher.patch(
      `${config.apiUrl}/inventory/goods-receipts/${id}`,
      input,
    );
  },
  async previewCosts(input: PreviewLandedCostDTO): Promise<LandedCostPreview> {
    return fetcher.post(
      `${config.apiUrl}/inventory/goods-receipts/preview-costs`,
      input,
    );
  },
  async confirmReceipt(id: string): Promise<GoodsReceiptDetailDTO> {
    return fetcher.post(
      `${config.apiUrl}/inventory/goods-receipts/${id}/confirm`,
      {},
    );
  },
  async duplicateReceipt(id: string): Promise<GoodsReceiptDetailDTO> {
    return fetcher.post(
      `${config.apiUrl}/inventory/goods-receipts/${id}/duplicate`,
      {},
    );
  },
  async voidReceipt(id: string): Promise<GoodsReceiptDetailDTO> {
    return fetcher.post(
      `${config.apiUrl}/inventory/goods-receipts/${id}/void`,
      {},
    );
  },
  async deleteReceipt(
    id: string,
  ): Promise<GoodsReceiptDetailDTO | { deleted: true; id: string }> {
    return fetcher.delete(`${config.apiUrl}/inventory/goods-receipts/${id}`);
  },
  async getInTransitSummary(): Promise<InTransitSummaryDTO> {
    return fetcher.get(`${config.apiUrl}/inventory/summary/in-transit`);
  },
  async getReorderHints(limit = 20): Promise<ReorderHintsDTO> {
    const url = new URL(`${config.apiUrl}/inventory/summary/reorder-hints`);
    url.searchParams.set("limit", String(limit));
    return fetcher.get(url.toString());
  },
  async getReorderPlan(query: ReorderPlanQueryDTO): Promise<ReorderPlanResultDTO> {
    const url = new URL(`${config.apiUrl}/inventory/planning/reorder`);
    if (query.from) url.searchParams.set("from", query.from);
    if (query.to) url.searchParams.set("to", query.to);
    if (query.targetDaysOfCover) {
      url.searchParams.set("targetDaysOfCover", String(query.targetDaysOfCover));
    }
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.productId) url.searchParams.set("productId", query.productId);
    if (query.variantId) url.searchParams.set("variantId", query.variantId);
    return fetcher.get(url.toString());
  },
  async getStockWithoutLotSummary(
    limit = 20,
  ): Promise<StockWithoutLotSummaryDTO> {
    const url = new URL(`${config.apiUrl}/inventory/summary/stock-without-lot`);
    url.searchParams.set("limit", String(limit));
    return fetcher.get(url.toString());
  },
  async getVariantStockLots(
    variantId: string,
    includeDepleted = false,
  ): Promise<{ data: StockLotDetailDTO[] }> {
    const url = new URL(
      `${config.apiUrl}/inventory/stock-lots/by-variant/${variantId}`,
    );
    if (includeDepleted) url.searchParams.set("includeDepleted", "true");
    return fetcher.get(url.toString());
  },
  async previewBundleAssembly(
    query: { bundleVariantId: string; quantity: number },
  ): Promise<BundleAssemblyPreviewDTO> {
    const url = new URL(`${config.apiUrl}/inventory/bundle-assembly/preview`);
    url.searchParams.set("bundleVariantId", query.bundleVariantId);
    url.searchParams.set("quantity", String(query.quantity));
    return fetcher.get(url.toString());
  },
  async getBundleAssemblableQty(variantId: string): Promise<{
    variantId: string;
    maxAssemblableQty: number;
  }> {
    return fetcher.get(
      `${config.apiUrl}/inventory/bundles/${variantId}/assemblable-qty`,
    );
  },
  async assembleBundle(
    input: BundleAssemblyDTO,
  ): Promise<BundleAssemblyResultDTO> {
    return fetcher.post(`${config.apiUrl}/inventory/bundle-assembly`, input);
  },
  async previewBundleDisassembly(
    query: { bundleVariantId: string; quantity: number },
  ): Promise<BundleDisassemblyPreviewDTO> {
    const url = new URL(
      `${config.apiUrl}/inventory/bundle-disassembly/preview`,
    );
    url.searchParams.set("bundleVariantId", query.bundleVariantId);
    url.searchParams.set("quantity", String(query.quantity));
    return fetcher.get(url.toString());
  },
  async disassembleBundle(
    input: BundleDisassemblyDTO,
  ): Promise<BundleDisassemblyResultDTO> {
    return fetcher.post(`${config.apiUrl}/inventory/bundle-disassembly`, input);
  },
};
